import { useCallback, useEffect, useMemo, useState } from "react";

import { PaymentsPayoutService } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import useTranslations from "@/shared/lang/useTranslations";
import { downloadBlob } from "@/shared/utils/downloadBlob";
import { canShareFiles, shareFile } from "@/shared/utils/shareImage";
import {
  renderTransferReceiptImage,
  renderTransferReceiptPdf,
} from "@/shared/utils/transferReceipt/renderTransferReceipt";
import {
  buildTransferReceipt,
  type TransferReceiptCopy,
  type TransferReceiptModel,
} from "@/shared/utils/transferReceipt/transferReceipt";

interface RenderedReceipt {
  key: string;
  model: TransferReceiptModel | null;
  image: Blob | null;
  imageUrl: string | null;
  pdf: Blob | null;
}

export type TransferReceiptFormat = "image" | "pdf";

const FORMATS = {
  image: { type: "image/png", extension: "png" },
  pdf: { type: "application/pdf", extension: "pdf" },
} as const;

export function useTransferReceiptCopy(): TransferReceiptCopy {
  const { t } = useTranslations("transferReceipt");
  return useMemo(
    () => ({
      title: t("title"),
      status: { settled: t("status_settled"), pending: t("status_pending"), rejected: t("status_rejected") },
      notice: { pending: t("notice_pending"), rejected: t("notice_rejected") },
      amountLabel: {
        settled: t("amount_settled"),
        pending: t("amount_pending"),
        rejected: t("amount_rejected"),
      },
      via: t("via"),
      number: t("number"),
      networkReference: t("network_reference"),
      to: t("to"),
      from: t("from"),
      name: t("name"),
      entity: t("entity"),
      reference: t("reference"),
      hash: t("hash"),
      network: t("network"),
      document: t("document"),
      account: t("account"),
      accountValue: (currency) => t("account_value", { currency }),
      sentThrough: t("sent_through"),
      senderEntity: t("sender_entity"),
      footer: t("footer"),
      filePrefix: t("file_prefix"),
      destination: {
        BREB_KEY: t("destination_breb_key"),
        PIX_KEY: t("destination_pix_key"),
        BANK_ACCOUNT: t("destination_bank_account"),
        CLABE: t("destination_clabe"),
        CBU_CVU: t("destination_cbu_cvu"),
        CRYPTO_WALLET: t("destination_crypto_wallet"),
      },
      savingsAccount: t("savings_account"),
      checkingAccount: t("checking_account"),
      alias: t("alias"),
      shareText: (amount) => t("share_text", { amount }),
    }),
    [t],
  );
}

/**
 * The comprobante of one payout as an image and a PDF, read from `GET /payouts/:id` so every surface prints
 * the same thing. Both files are drawn as soon as the payout is read, not on the click: Safari only opens the
 * share sheet from a user gesture, and drawing first would spend it. `status` re-reads when the payout
 * moves.
 */
export function useTransferReceipt(
  payoutId: string | null,
  { status, organizationName = null }: { status?: string | null; organizationName?: string | null } = {},
) {
  const { activeLanguage } = useTranslations("transferReceipt");
  const copy = useTransferReceiptCopy();
  const [rendered, setRendered] = useState<RenderedReceipt | null>(null);
  const key = payoutId ? `${payoutId}:${status ?? ""}:${activeLanguage}` : null;

  useEffect(() => {
    if (!payoutId || !key) return;
    let cancelled = false;
    let imageUrl: string | null = null;
    void (async () => {
      try {
        const { data } = await PaymentsPayoutService.getPayout(payoutId);
        const model = buildTransferReceipt({ payout: data, organizationName, locale: activeLanguage, copy });
        const [image, pdf] = await Promise.all([
          renderTransferReceiptImage(model).catch(() => null),
          renderTransferReceiptPdf(model).catch(() => null),
        ]);
        if (cancelled) return;
        imageUrl = image ? URL.createObjectURL(image) : null;
        setRendered({ key, model, image, imageUrl, pdf });
      } catch {
        if (!cancelled) setRendered({ key, model: null, image: null, imageUrl: null, pdf: null });
      }
    })();
    return () => {
      cancelled = true;
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [activeLanguage, copy, key, organizationName, payoutId]);

  const current = key && rendered?.key === key ? rendered : null;

  const deliver = useCallback(
    async (format: TransferReceiptFormat, mode: "share" | "download") => {
      const file = format === "image" ? current?.image : current?.pdf;
      if (!current?.model || !file) return null;
      const { type, extension } = FORMATS[format];
      const fileName = `${current.model.fileBase}.${extension}`;
      if (mode === "download") {
        downloadBlob(file, fileName);
        return "downloaded" as const;
      }
      return shareFile(file, {
        fileName,
        type,
        title: current.model.shareTitle,
        text: current.model.shareText,
      });
    },
    [current],
  );

  return {
    loading: key !== null && current === null,
    failed: current !== null && (current.model === null || (!current.image && !current.pdf)),
    hasImage: !!current?.image,
    hasPdf: !!current?.pdf,
    imageUrl: current?.imageUrl ?? null,
    canShare: { image: canShareFiles(FORMATS.image.type), pdf: canShareFiles(FORMATS.pdf.type) },
    deliver,
  };
}
